<script setup lang="ts">
import { ChatBubbleBottomCenterTextIcon, XCircleIcon } from '@heroicons/vue/24/solid';
import { publicContainerApi } from '@libero/api-client/public-container/api';
import { publicTicketApi } from '@libero/api-client/public-ticket/api';
import { StorePublicTicketRequest } from '@libero/api-client/public-ticket/types';
import { useMapContext } from '@libero/citizen-report/modules/map/hooks/useMap';
import Map from '@libero/citizen-report/modules/map/views/segments/Map.vue';
import Replace from '@libero/citizen-report/modules/report/views/modals/Replace.vue';
import Subscribe from '@libero/citizen-report/modules/report/views/modals/Subscribe.vue';
import { useTenantStore } from '@libero/hooks/store/useTenantStore';
import { useFormContext } from '@libero/hooks/useForm';
import { useGeocoder } from '@libero/hooks/useGeocoder';
import { useGeolocation } from '@libero/hooks/useGeolocation';
import ThemeIcon from '@libero/organisms/ThemeIcon/ThemeIcon.vue';
import { Color } from '@libero/types/Color';
import Alert from '@libero/ui-framework/Alert/Alert.vue';
import Button from '@libero/ui-framework/Button/Button.vue';
import Checkbox from '@libero/ui-framework/Checkbox/Checkbox.vue';
import Cluster from '@libero/ui-framework/Cluster/Cluster.vue';
import FormLayout from '@libero/ui-framework/FormLayout/FormLayout.vue';
import Icon from '@libero/ui-framework/Icon/Icon.vue';
import IconButton from '@libero/ui-framework/IconButton/IconButton.vue';
import InputGroup from '@libero/ui-framework/InputGroup/InputGroup.vue';
import Label from '@libero/ui-framework/Label/Label.vue';
import Link from '@libero/ui-framework/Link/Link.vue';
import List from '@libero/ui-framework/List/List.vue';
import ListItem from '@libero/ui-framework/List/ListItem.vue';
import Select from '@libero/ui-framework/Select/Select.vue';
import Spinner from '@libero/ui-framework/Spinner/Spinner.vue';
import Stack from '@libero/ui-framework/Stack/Stack.vue';
import SubLabel from '@libero/ui-framework/SubLabel/SubLabel.vue';
import Tooltip from '@libero/ui-framework/Tooltip/Tooltip.vue';
import Typography from '@libero/ui-framework/Typography/Typography.vue';
import { keepPreviousData, useQuery } from '@tanstack/vue-query';
import { AllGeoJSON, pointOnFeature } from '@turf/turf';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  isLoading: boolean;
  hasInlineMap: boolean;
  onPreviousStep: () => void;
  onNextStep: () => void;
}

defineProps<Props>();

const { t } = useI18n();
const { tenant } = useTenantStore();

const form = useFormContext<StorePublicTicketRequest>();
const map = useMapContext();

const themeId = computed(() => form.values.theme_id);

const geoLocation = useGeolocation();
const geocoder = useGeocoder(map.updateCoordinates, map.toggle, themeId);

const coordinates = ref<string>();
const nearbyContainer = ref<string>();
const tempSavedContainerId = ref<string>();
const isOpen = ref(false);

const containerIds = computed(() => form.values.containers);

const { data: containers } = useQuery({
  queryKey: ['containers', containerIds],
  queryFn: () => {
    if (!containerIds.value.length) return null;

    return publicContainerApi.index({
      filter: { id: form.values.containers.join(',') },
    });
  },
  placeholderData: keepPreviousData,
});

const { data: nearbyContainers } = useQuery({
  queryKey: ['nearby-containers', coordinates],
  queryFn: () =>
    publicContainerApi.index({
      filter: {
        nearby: coordinates.value,
      },
    }),
  placeholderData: keepPreviousData,
});

const unMountCallbacks = ref<(() => void)[]>([]);

const ticketSubjectId = computed(() => form.values.ticket_subject_id);

const { data: ticketSubjects } = useQuery({
  queryKey: ['ticket-subjects', themeId],
  queryFn: () => {
    if (!themeId.value) return null;
    return publicTicketApi.subject(themeId.value);
  },
});

const ticketSubject = computed(() =>
  ticketSubjects.value?.find((subject) => subject.id === ticketSubjectId.value),
);

const handleAddContainer = (id: string) => {
  if (ticketSubject.value?.is_multiple_containers === false && form.values.containers.length >= 1) {
    isOpen.value = true;
    tempSavedContainerId.value = id;
    map.removeContainer(id);
  } else {
    form.values.containers.push(id);
  }
};

const replaceContainer = () => {
  if (!tempSavedContainerId.value || !form.values.containers.at(0)) return;

  map.removeContainer(form.values.containers.at(0));
  map.addContainer(tempSavedContainerId.value);
  tempSavedContainerId.value = '';
  isOpen.value = false;
};

const handleRemoveContainer = (id?: string) => {
  form.values.containers = form.values.containers.filter((i) => i !== id);
};

const handleToggleContainer = (id?: string) => {
  if (!id) return;

  map.toggle(id);

  nearbyContainer.value = id;
  nextTick(() => (nearbyContainer.value = undefined));

  const container = nearbyContainers.value?.find((container) => container.id === id);
  const point = container?.geometry && pointOnFeature(container.geometry as AllGeoJSON);

  if (point) {
    const [longitude, latitude] = point.geometry.coordinates || [];
    map.updateCoordinates([longitude, latitude]);
  }
};

const theme = computed(() => tenant?.themes.find((theme) => theme.id === form.values.theme_id));
const hasTickets = computed(() => containers.value?.some((container) => container.tickets.length));

const isDisabled = computed(
  () => !form.values.containers.length || (hasTickets.value && !form.values.force_create),
);

const handleCancel = () => {
  isOpen.value = false;

  if (tempSavedContainerId.value) {
    map.removeContainer(tempSavedContainerId.value);
    tempSavedContainerId.value = undefined;
  }
};

const handleMove = (coords: [number, number]) => {
  coordinates.value = `${coords[1]},${coords[0]}`;
};

watch(geocoder, () => {
  coordinates.value = `${geocoder.latitude},${geocoder.longitude}`;
});

onMounted(() => {
  geoLocation.onResult(geocoder.handleCoordinates);
  unMountCallbacks.value.push(map.onAddContainer(handleAddContainer));
  unMountCallbacks.value.push(map.onRemoveContainer(handleRemoveContainer));
  unMountCallbacks.value.push(map.onMove(handleMove));

  if (form.values.theme_id) {
    map.setThemeId(form.values.theme_id);
  }
});

onUnmounted(() => {
  unMountCallbacks.value.forEach((callback) => callback());
});
</script>

<template>
  <Typography tag="h1" type="heading" size="xl" bold>
    {{ t('citizen-report.location-title') }}
  </Typography>

  <Stack :gap="2">
    <Typography size="lg">
      {{ t('citizen-report.location-description') }}
    </Typography>

    <FormLayout>
      <InputGroup>
        <Label for="geocoder" :value="t('report.search')" size="xl" />
        <SubLabel>{{ t('report.search-note') }}</SubLabel>

        <Select
          size="lg"
          :placeholder="t('report.search')"
          hasSearchIcon
          :filterOption="false"
          v-bind="geocoder.register()"
        />

        <Cluster :gap="0.5" alignItems="center" minHeight="1.75rem">
          <Link :isDisabled="geoLocation.isLoading" semiBold :onClick="geoLocation.getLocation">
            {{ t('citizen-report.use-my-location') }}
          </Link>

          <Spinner v-if="geoLocation.isLoading" size="xs" />
        </Cluster>
      </InputGroup>

      <Map v-if="hasInlineMap" />

      <InputGroup v-if="false && nearbyContainers?.length">
        <Label for="containers" :value="t('citizen-report.containers')" size="xl" />

        <Select
          id="containers"
          name="containers"
          :value="nearbyContainer"
          size="lg"
          :options="
            nearbyContainers
              ?.filter((container) => !form.values.containers.includes(container.id))
              .map((container) => ({
                label: container.name,
                value: container.id,
              }))
          "
          :onUpdate="handleToggleContainer"
        />
      </InputGroup>

      <InputGroup>
        <Label for="location" :value="t('citizen-report.selected-locations')" size="xl" />

        <Stack :gap="0.25">
          <List v-for="container in containers" :key="container.id" size="lg">
            <ListItem>
              <Stack :gap="0.5">
                <Cluster justifyContent="space-between" alignItems="center">
                  <Cluster :gap="0.75" alignItems="center">
                    <ThemeIcon v-if="theme" :theme="theme.key" size="lg" :color="Color.gray500" />

                    <Typography size="lg" bold>
                      {{ container.name }}
                    </Typography>
                  </Cluster>

                  <Tooltip :content="t('deselect')">
                    <IconButton
                      appearance="link"
                      size="sm"
                      color="secondary"
                      :ariaLabel="t('report.remove-container', { name: container.name })"
                      :onClick="() => map.removeContainer(container.id)"
                    >
                      <XCircleIcon />
                    </IconButton>
                  </Tooltip>
                </Cluster>

                <template v-if="container.tickets.length">
                  <Alert v-if="container.tickets.length" type="warning">
                    <strong>
                      {{ t('please-note') }}
                    </strong>

                    {{ t('report.has-report') }}
                  </Alert>

                  <Stack v-for="ticket in container.tickets" :key="ticket.id" :gap="0.25">
                    <Cluster alignItems="center">
                      <Icon size="lg">
                        <ChatBubbleBottomCenterTextIcon />
                      </Icon>

                      <Typography size="lg">
                        {{ ticket.ticket_subject?.name || '-' }}
                      </Typography>
                    </Cluster>

                    <Cluster minHeight="2.5rem" alignItems="center" :gap="0.5">
                      <Button
                        size="sm"
                        appearance="outline"
                        :to="{
                          name: 'report.show',
                          params: {
                            containerId: container.id,
                            id: ticket.id,
                          },
                        }"
                        :ariaLabel="t('report.info-aria', { container: container.name })"
                      >
                        {{ t('report.info') }}
                      </Button>

                      <Subscribe v-slot="{ openModal }" :ticketId="ticket.id">
                        <Button
                          size="sm"
                          :onClick="openModal"
                          :ariaLabel="
                            t('report.subscribe-ticket', {
                              ticket: ticket.ticket_subject?.name,
                              container: container.name,
                            })
                          "
                        >
                          {{ t('report.subscribe') }}
                        </Button>
                      </Subscribe>
                    </Cluster>
                  </Stack>
                </template>
              </Stack>
            </ListItem>
          </List>

          <template v-if="!containers?.length">
            <Alert v-if="nearbyContainers?.length" type="info" size="lg">
              {{ t('citizen-report.select-location-on-map-or-select') }}
            </Alert>

            <Alert v-else type="info" size="lg">
              {{ t('citizen-report.select-location-on-map') }}
            </Alert>
          </template>
        </Stack>
      </InputGroup>

      <InputGroup v-if="hasTickets">
        <Checkbox size="lg" v-bind="form.register('force_create')">
          {{ t('citizen-report.duplicate-ticket') }}
        </Checkbox>
      </InputGroup>
    </FormLayout>

    <Cluster justifyContent="space-between">
      <Button size="lg" appearance="outline" :onClick="onPreviousStep">
        {{ t('previous') }}
      </Button>

      <Button size="lg" :isDisabled="isDisabled" :isLoading="isLoading" :onClick="onNextStep">
        {{ t('next') }}
      </Button>
    </Cluster>
  </Stack>

  <Replace
    v-if="ticketSubject"
    :isOpen="isOpen"
    :onCancel="handleCancel"
    :onReplace="replaceContainer"
    :ticketSubject="ticketSubject"
  />
</template>
